<script>
import analytics from '../utils/analytics.js'
import config from '../config.js'
import { onMount } from 'svelte'
import Overlay from '../components/Overlay.svelte'
import { overlay, sidebarToggle } from '../stores.js'
import Icon from '../components/Icon.svelte'
import satsSymbol from '../assets/icon/satoshi.svg'

function goToContact () {
  $overlay = null
  $sidebarToggle = 'contact'
}

</script>

<style type="text/scss">
  .info {
    text-align: left;

    h2 {
      text-align: center;
      letter-spacing: 0.5em;
    }

    .faq {
      border-top: solid 1px white;
      margin: 2em 0;

      a {
        color: var(--light-bold-a);
      }

      a:visited {
        color: var(--bold-a);
      }

      p.q {
        font-size: 1.3em;
        font-weight: 500;
      }

      p.a {
        margin-bottom: 0;

        &:last-child {
          margin-bottom: 1em;
        }
      }

      .sat-sym {
        color: var(--bitcoin-orange);
      }

      ol, ul {
        margin-top: 0;
      }
      ol {
        counter-reset: list;
      }
      ol > li {
        list-style: none;
      }
      ol > li:before {
        content: counter(list, lower-alpha) ") ";
        counter-increment: list;
      }
    }
  }
</style>

<Overlay name="about">
  <section class="info">
    <h2>FAQ</h2>
    <div class="faq">
      <p class="q">
        What is this?
      </p>
      <p class="a">
        One bitcoin is made up of 100 million units, called satoshis or sats.
      </p>
      <p class="a">
        Coin Bits compares the value of satoshis to the value of physical coins from fiat currencies around the world by showing you exactly how many coins each satoshi is worth, and vice versa.
      </p>
      <p class="a">
        For each currency, where possible, Coin Bits presents the smallest denomination coin that is:
      </p>
      <ol><li>still legal tender, and </li><li>possibly in active circulation.</li></ol>
    </div>
    <div class="faq">
      <p class="q">
        OK, but why?
      </p>
      <p class="a">
        As the price of bitcoin continues to rise, it becomes less and less convenient to measure prices directly in ₿ or even in smaller units like mBTC.
      </p>
      <p class="a">
        We need a new standard for pricing, and that standard is the humble <Icon icon={satsSymbol} inline color="var(--bitcoin-orange)" /> satoshi. Satoshis are already the default unit of account on the Lightning Network and in several popular bitcoin wallets.
      </p>
      <p class="a">
        Coin Bits supports the push for the satoshi standard by demonstrating that sats are now comparable in value to physical coins representing the smallest practical divisions of major fiat currencies.
      </p>
    </div>
    <div class="faq">
      <p class="q">
        How do I navigate the site?
      </p>
      <p class="a">
        Currencies are sorted by the relative value of their listed coins, in ascending order (i.e. more valuable coins to the right).
      </p>
      <p class="a">
        Click the forward or back arrows to scroll through the coins one by one, or type in the search bar and select a result to jump directly to that currency.
      </p>
      <p class="a">
        Flip between sats and fiat as the base for comparison by clicking on the equals sign in the middle.
      </p>
    </div>
    <div class="faq">
      <p class="q">
        How did you make this?
      </p>
      <p class="a">
        Coin Bits is a static site developed in Svelte. There is no back end, but exchange rate data is kindly provided by <a href="https://fixer.io/" target="_blank">fixer.io</a> and <a href="https://blockchain.info/" target="_blank">blockchain.info</a>.
      </p>
    </div>
    <div class="faq">
      <p class="q">
        Where is my favorite currency/coin?
      </p>
      <p class="a">
        I attempted to include a coin from every circulating national or territorial currency in the world. If a currency is not included, it's either because no coins exist (like the Congolese Franc), or inflation has rendered any coins completely worthless (like the Venezuelan Bolívar Soberano).
      </p>
      <p class="a">
        The currencies of a few smaller territories (like the Guernsey Pound) or disputed states (like the Abkhazian Apsar) are also missing, either due to laziness on my part or because reliable exchange rates aren't available.
      </p>
    </div>
    <div class="faq">
      <p class="q">
        What's that weird symbol?
      </p>
      <p class="a">
        This one <Icon icon={satsSymbol} inline color="var(--bitcoin-orange)" />? It's a satoshi symbol proposed by <a href="https://satsymbol.com/" target="_blank">SatsSymbol.com</a>.
      </p>
      <p class="a">
        Unlike the Bitcoin <span class="sat-sym">₿</span> sign, there is no 'official' or universally adopted sign for the satoshi.
      </p>
      <p class="a">
        <a href="https://www.lolli.com/" target="_blank">Lolli</a> popularised <span class="sat-sym">§</span>, and some wallets use <span class="sat-sym">Ş</span> or <span class="sat-sym">$</span> or <span class="sat-sym">＄</span> or <span class="sat-sym">s</span> to represent sats, but the jury is still out.
      </p>
      <p>Personally I think <Icon icon={satsSymbol} inline color="var(--bitcoin-orange)" /> looks great, works best for a coin-like presentation where unicode support isn't required, and avoids confusion with dollars or other fiat currency symbols.</p>
    </div>
    <div class="faq">
      <p class="q">
        Why are you being wrong about coins on the internet?
      </p>
      <p class="a">
        Sorry! I'm not an expert numismatician, but I have tried to make the site as accurate and informative as possible.
      </p>
      <p class="a">
        In my defence, during the course of building the site I read every wikipedia page for each of the <a href="https://en.wikipedia.org/wiki/List_of_circulating_currencies" target="_blank">180+ circulating world currencies</a>, scoured coin enthusiast forums, and trawled the awful government websites of more central banks and national mints than I care to remember.
      </p>
      <p class="a">
        Despite that, some questions are curiously and stubbornly hard to answer from internet research alone:
      </p>
      <ul>
        <li>Does the currency symbol go before or after the amount?</li>
        <li>Is that coin really still legal tender?</li>
        <li>Has anyone in Thailand ever seen a genuine 1 satang coin in real life?</li>
      </ul>
      <p class="a">
        Any and all corrections are gladly received! Check the <a href="#" on:click={goToContact}>contact tab</a> for ways to get in touch.
      </p>
    </div>
    <div class="faq">
      <p class="q">
        Something is broken!
      </p>
      <p class="a">
        Oops. Please check the <a href="#" on:click={goToContact}>contact tab</a> for ways to let me know what's wrong, thanks!
      </p>
    </div>
</Overlay>
